/**
 * Discover — the tenant's course catalogue.
 *
 * Layout follows the prototype's catalogue screen and reuses Landing's vocabulary so the
 * two read as one product: a compact brand-gradient band carrying the heading and the
 * search, then a card grid on the tinted ground.
 *
 * The catalogue is fetched one page per org (see `listCatalogue`) and paged with an
 * explicit "Load more" rather than scroll-triggered loading — a footer with real links
 * lives under this grid, and infinite scroll makes it unreachable.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Banner, Button, EmptyState, Icon, SearchField } from '../../shared/ui';
import { getConfig, text } from '../../shared/lib/config';
import {
  courseHref,
  courseImage,
  listCatalogue,
  startLabel,
  type CatalogueCourse,
} from '../../shared/lib/catalogue';
import { coursePriceView, type PricingSeam } from '../../shared/lib/pricing';
import { useCoursePrices } from '../../shared/lib/usePricing';
import { PriceTag } from '../../shared/ui/price';
import './learner.css';

/** Three cards a row at the prototype's 1200px container, so pages arrive whole. */
const PAGE_SIZE = 12;

export default function Discover() {
  const config = getConfig();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounced so typing does not fire a request per keystroke against the search index.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const query = useInfiniteQuery({
    queryKey: ['catalogue', search, config.tenant.orgs],
    queryFn: ({ pageParam }) => listCatalogue({ search, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (last.hasMore ? all.length + 1 : undefined),
    // The catalogue is public, so this renders for signed-out visitors too.
    retry: false,
  });

  const courses = query.data?.pages.flatMap((page) => page.courses) ?? [];
  const total = query.data?.pages[0]?.count ?? 0;

  /**
   * PRICES FOR THE CARDS THAT EXIST, AND ONLY THOSE (spec R6.3, AC-023).
   *
   * The keys are the loaded pages' ids, so "Load more" widens the batch by twelve rather
   * than re-asking for the whole catalogue: the sorted-joined cache key means page 1's
   * twelve stay cached and page 2 costs one request for twenty-four. That is the middle
   * road `pricing.ts` argues for — one request per SCREENFUL, never one per card, and never
   * the academy's whole catalogue.
   *
   * `useCoursePrices` is disabled while the list is empty, so the pending and error branches
   * below cost no pricing request at all.
   */
  const visibleIds = useMemo(() => courses.map((course) => course.id), [courses]);
  const prices = useCoursePrices(visibleIds);

  return (
    <main className="cat">
      <header className="cat__band">
        <div className="cat__band-inner">
          <p className="cat__eyebrow">{text('nav_discover', 'Discover')}</p>
          <h1>{text('catalogue_heading', 'Every course in the academy')}</h1>
          <p className="cat__lede">
            {text(
              'catalogue_subheading',
              'Browse what is on offer and enrol in whatever you need next.',
            )}
          </p>
          <div className="cat__search">
            <SearchField value={input} onChange={setInput} placeholder="Search courses…" />
          </div>
        </div>
      </header>

      <div className="cat__body">
        {query.isPending ? (
          <CardGridSkeleton />
        ) : query.isError ? (
          <Banner tone="danger">
            <strong>The catalogue could not be loaded.</strong> The academy did not answer.
            This is not something you have done — nothing has changed, so retrying is safe.
            <div className="cat__banner-action">
              <Button variant="secondary" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </div>
          </Banner>
        ) : courses.length === 0 ? (
          search ? (
            <EmptyState
              title={`Nothing matches “${search}”`}
              body="Try a shorter word, or clear the search to see the whole catalogue."
              action={
                <Button variant="secondary" onClick={() => setInput('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No courses are published yet"
              body="This academy has not published anything to the catalogue. Courses appear here the moment they go live."
            />
          )
        ) : (
          <>
            <p className="cat__count" role="status">
              {search
                ? `${courses.length} ${courses.length === 1 ? 'course matches' : 'courses match'} “${search}”`
                : `${total} ${total === 1 ? 'course' : 'courses'}`}
            </p>

            <div className="course-grid">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} prices={prices} />
              ))}
            </div>

            {query.hasNextPage && (
              <div className="cat__more">
                <Button
                  variant="secondary"
                  busy={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  Load more courses
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Catalogue card.
 *
 * A whole-card link rather than a link on the title: the card is one target, and making
 * only the heading clickable is the commonest reason people think a grid is not
 * interactive.
 */
function CourseCard({
  course,
  prices,
}: {
  course: CatalogueCourse;
  prices: PricingSeam | undefined;
}) {
  const start = startLabel(course);

  /**
   * THE PRICE IS READ THROUGH THE SEAM, NEVER DERIVED HERE (spec R6.0).
   *
   * `coursePriceView` is the only thing that knows the difference between this course being
   * ABSENT from a price document that loaded — free, because an unpriced course is free —
   * and there being no document at all, which is `unknown` and renders "Price unavailable".
   * While the batch is in flight `prices` is `undefined`, which is the second case: a card
   * says "Price unavailable" for a moment rather than saying "Free" and then correcting
   * itself to a number, which is the one direction the mistake is unrecoverable in.
   */
  const price = coursePriceView(prices, course.id);

  return (
    <Link to={courseHref(course.id)} className="course-card">
      <CourseThumb name={course.name} src={courseImage(course.media)} />

      <div className="course-card__body">
        <span className="course-card__org">{course.org}</span>
        <h3>{course.name}</h3>
        {course.short_description && <p>{course.short_description}</p>}

        <div className="course-card__meta">
          {/*
           * PRICE FIRST IN THE META ROW (R6.3), because it is the fact that decides whether
           * the reader opens the card at all — a start date matters only once they have
           * decided they can afford it.
           *
           * `PriceTag` renders "Free" as a WORD and never "RM 0.00" (R5.7), and renders the
           * unavailable state muted rather than as an alarm: the card is still worth opening,
           * and the course page states the reason (R6.4). No fee disclosure here — one line,
           * no disclosure, no comparison; a card has no room for a sentence and courses
           * carry no discount in this spec (R6.3).
           */}
          <PriceTag view={price} />
          {start && (
            <span>
              <Icon name="event" />
              {start}
            </span>
          )}
          {course.effort && (
            <span>
              <Icon name="schedule" />
              {course.effort}
            </span>
          )}
          {course.invitation_only && (
            <span>
              <Icon name="lock" />
              Invitation only
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Course artwork, with a branded tile behind it.
 *
 * The fallback is not decoration. Course images are optional in Studio, and the url the
 * API returns points at the LMS host whether or not the asset was ever uploaded — so a
 * missing image is the common case, not the edge case, and a grid of broken-image icons
 * is what you get without this. `onError` covers both: no url at all, and a url that
 * does not resolve.
 *
 * Exported because the dashboard renders the same tile, and the two drifting apart would
 * show the same course two different ways on two pages.
 */
export function CourseThumb({ name, src }: { name: string; src: string | null }) {
  const [failed, setFailed] = useState(false);

  if (src && !failed) {
    return (
      <img
        className="course-card__thumb"
        src={src}
        // Decorative: the course name is already the card's heading, so announcing it
        // again as alt text just makes a screen reader say everything twice.
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="course-card__thumb course-card__thumb--letter" aria-hidden="true">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/** Placeholders in the card's own shape, so the grid does not reflow when data lands. */
function CardGridSkeleton() {
  return (
    <div className="course-grid" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div className="course-card course-card--skeleton" key={index}>
          <div className="course-card__thumb" />
          <div className="course-card__body">
            <span className="skeleton skeleton--chip" />
            <span className="skeleton skeleton--title" />
            <span className="skeleton skeleton--line" />
          </div>
        </div>
      ))}
    </div>
  );
}
